import chroma from 'chroma-js';
import z from 'zod';

const {
  // VITE_MAPBOX_USERNAME: MAPBOX_USERNAME,
  // VITE_MAPBOX_STYLE_ID: MAPBOX_STYLE_ID,
  // VITE_MAPBOX_ACCESS_TOKEN: MAPBOX_ACCESS_TOKEN,
  VITE_BACKEND_BASE_URL: BACKEND_BASE_URL,
} = import.meta.env;

export enum SourceType {
  TilesTMS = 'tiles-tms',
  GeoRaster = 'georaster',
  GeoJson = 'geo-json',
  None = 'none',
}
const Bounds = z.tuple([
  z.tuple([z.number(), z.number()]),
  z.tuple([z.number(), z.number()]),
]);

export const DataLayerIdSchema = z.string();
export type DataLayerId = z.infer<typeof DataLayerIdSchema>;

export const ProjectIdSchema = z.string();
export type ProjectId = z.infer<typeof ProjectIdSchema>;

export const SubcategoryIdSchema = z.string();
export type SubcategoryId = z.infer<typeof SubcategoryIdSchema>;

export const CategoryIdSchema = z.string();
export type CategoryId = z.infer<typeof CategoryIdSchema>;

const SourceTilesTMSSchema = z.object({
  type: z.literal(SourceType.TilesTMS),
  url: z.string(),
  minZoom: z.number(),
  maxZoom: z.number(),
  minValue: z.number(),
  maxValue: z.number(),
  bounds: Bounds,
});
export type SourceTilesTMS = z.infer<typeof SourceTilesTMSSchema>;

const SourceGeoRasterSchema = z.object({
  type: z.literal(SourceType.GeoRaster),
  url: z.string(),
});
export type SourceGeoRaster = z.infer<typeof SourceGeoRasterSchema>;

const SourceGeoJsonSchema = z.object({
  type: z.literal(SourceType.GeoJson),
  parameterForPropertySelectorColoring: z.string().optional().nullable(),
  propertyForSelectorColoring: z.string().optional().nullable(),
  url: z.string(),
  bounds: Bounds,
});
export type SourceGeoJson = z.infer<typeof SourceGeoJsonSchema>;

export const SourceNoneSchema = z.object({
  type: z.literal(SourceType.None),
});
export type SourceNone = z.infer<typeof SourceNoneSchema>;

export const SourceSchema = z.discriminatedUnion('type', [
  SourceTilesTMSSchema,
  SourceGeoRasterSchema,
  SourceGeoJsonSchema,
  SourceNoneSchema,
]);
export type Source = z.infer<typeof SourceSchema>;

export enum FlatLegendType {
  Flat = 'flat',
  TransparencyScale = 'transparency-scale',
}

export enum ColoringType {
  None = 'none',
  Flat = 'flat',
  Categories = 'categories',
  Bands = 'bands',
  Scale = 'scale',
}
export const ColoringNoneSchema = z.object({
  type: z.literal(ColoringType.None),
});
export type ColoringNone = z.infer<typeof ColoringNoneSchema>;

export const FlatLegendSolidSchema = z.object({
  type: z.literal(FlatLegendType.Flat),
  label: z.string(),
});
export type FlatLegendSolid = z.infer<typeof FlatLegendSolidSchema>;
export const FlatLegendTransparentSchema = z.object({
  type: z.literal(FlatLegendType.TransparencyScale),
  minLabel: z.string(),
  maxLabel: z.string(),
});
export type FlatLegendTransparent = z.infer<typeof FlatLegendTransparentSchema>;

export const ColoringFlatSchema = z.object({
  type: z.literal(ColoringType.Flat),
  color: z.string(),
  legend: z.discriminatedUnion('type', [
    FlatLegendSolidSchema,
    FlatLegendTransparentSchema,
  ]),
});
export type ColoringFlat = z.infer<typeof ColoringFlatSchema>;

export const ColoringBandsFirstBandSchema = z.object({
  color: z.string(),
  legend: z.string(),
});
export type ColoringBandsFirstBand = z.infer<
  typeof ColoringBandsFirstBandSchema
>;

export const ColoringBandsRestOfBandsSchema = z.object({
  startingAt: z.number(),
  color: z.string(),
  legend: z.string(),
});
export type ColoringBandsRestOfBands = z.infer<
  typeof ColoringBandsRestOfBandsSchema
>;

export const ColoringBandsSchema = z.object({
  type: z.literal(ColoringType.Bands),
  bands: z
    .tuple([ColoringBandsFirstBandSchema])
    .rest(ColoringBandsRestOfBandsSchema),
});
export type ColoringBands = z.infer<typeof ColoringBandsSchema>;

export const ColorCategoriesSchema = z.object({
  type: z.literal(ColoringType.Categories),
  fallback: z.string(),
  colorMap: z.record(z.object({ color: z.string(), legend: z.string() })),
});
export type ColorCategories = z.infer<typeof ColorCategoriesSchema>;

export const ColorScaleSchema = z.object({
  type: z.literal(ColoringType.Scale),
  min: z.number(),
  max: z.number(),
  colors: z.array(z.string()).min(1),
  legend: z.object({ minLabel: z.string(), maxLabel: z.string() }),
});
export type ColorScale = z.infer<typeof ColorScaleSchema>;

export const ColoringSchema = z.discriminatedUnion('type', [
  ColoringNoneSchema,
  ColoringFlatSchema,
  ColoringBandsSchema,
  ColorCategoriesSchema,
  ColorScaleSchema,
]);
export type Coloring = z.infer<typeof ColoringSchema>;

export enum DataLayerType {
  PlainProject = 'plain-project',
  Story = 'story',
  Data = 'data',
}

export enum ParameterType {
  Slider = 'slider',
  Select = 'select',
  DependentChoice = 'dependent-choice',
  Coordinate = 'coordinate',
  Coupled = 'coupled',
}

export const ParameterSliderSchema = z.object({
  type: z.literal(ParameterType.Slider),
  label: z.string(),
  min: z.number(),
  max: z.number(),
  step: z.number(),
  initValue: z.number(),
});
export type ParameterSlider = z.infer<typeof ParameterSliderSchema>;

export enum ParameterSelectDisplayType {
  Choice = 'choice',
  Dropdown = 'dropdown',
  Slider = 'slider',
}
export const ParameterSelectSchema = z.object({
  type: z.literal(ParameterType.Select),
  label: z.string(),
  options: z.array(
    z.object({ value: z.string().or(z.number()), label: z.string() })
  ),
  displayAs: z.nativeEnum(ParameterSelectDisplayType),
  initValue: z.string().or(z.number()),
});
export type ParameterSelect = z.infer<typeof ParameterSelectSchema>;

export const ParameterDependentChoiceSchema = z.object({
  type: z.literal(ParameterType.DependentChoice),
  label: z.string(),
  subLabel: z.string(),
  options: z.array(
    z.object({
      value: z.string().or(z.number()),
      label: z.string(),
      subOptions: z.array(
        z.object({ value: z.string().or(z.number()), label: z.string() })
      ),
    })
  ),
  initValue: z.tuple([z.string().or(z.number()), z.string().or(z.number())]),
});
export type ParameterDependentChoice = z.infer<
  typeof ParameterDependentChoiceSchema
>;

export const ParameterCoordinateSchema = z.object({
  type: z.literal(ParameterType.Coordinate),
  label: z.string(),
  initValue: z.tuple([z.number(), z.number()]),
});
export type ParameterCoordinate = z.infer<typeof ParameterCoordinateSchema>;

export const ParameterCoupledSchema = z.object({
  type: z.literal(ParameterType.Coupled),
  dataLayerId: z.string(),
  parameterName: z.string(),
});
export type ParameterCoupled = z.infer<typeof ParameterCoupledSchema>;

export const DataLayerParameterSchema = z.discriminatedUnion('type', [
  ParameterSliderSchema,
  ParameterSelectSchema,
  ParameterDependentChoiceSchema,
  ParameterCoordinateSchema,
  ParameterCoupledSchema,
]);
export type DataLayerParameter = z.infer<typeof DataLayerParameterSchema>;

export enum DataLayerIcon {
  Story = 'story',
  Static = 'static',
  Series = 'series',
  Model = 'model',
}

export const MarkerLayerSchema = z.object({
  content: z.string(),
  contentDe: z.string().nullable(),
  markers: z.array(z.tuple([z.number(), z.number()])).min(1),
});
export type MarkerLayer = z.infer<typeof MarkerLayerSchema>;

export const DataLayerStorySchema = z.object({
  id: DataLayerIdSchema,
  type: z.literal(DataLayerType.Story),
  icon: z.literal(DataLayerIcon.Story),
  shortName: z.string(),
  shortNameDe: z.string().nullable(),
  defaultVisibility: z.boolean(),
  layers: z.array(MarkerLayerSchema).min(1),
});
export type DataLayerStory = z.infer<typeof DataLayerStorySchema>;

export const DataLayerDataSchema = z.object({
  id: DataLayerIdSchema,
  defaultVisibility: z.boolean(),
  icon: z
    .literal(DataLayerIcon.Static)
    .or(z.literal(DataLayerIcon.Model))
    .or(z.literal(DataLayerIcon.Series)),
  type: z.literal(DataLayerType.Data),
  shortName: z.string(),
  shortNameDe: z.string().nullable(),
  parameters: z.record(DataLayerParameterSchema),
  dataSources: z
    .object({
      dataSourceTriggers: z.array(z.string()),
      dataSource: SourceSchema,
      coloring: ColoringSchema,
    })
    .array(),
  plots: z
    .array(z.object({ url: z.string(), triggers: z.array(z.string()) }))
    .min(1)
    .optional()
    .nullable(),
});
export type DataLayerData = z.infer<typeof DataLayerDataSchema>;

export const DataLayerPlainSchema = z.object({
  id: DataLayerIdSchema,
  type: z.literal(DataLayerType.PlainProject),
  markers: z.array(z.tuple([z.number(), z.number()])).min(1),
});
export type DataLayerPlain = z.infer<typeof DataLayerPlainSchema>;

export const DataLayerSchema = z.discriminatedUnion('type', [
  DataLayerStorySchema,
  DataLayerDataSchema,
]);
export type DataLayer = z.infer<typeof DataLayerSchema>;

export enum ContentFormat {
  Text = 'text',
  Html = 'html',
}

export enum CategoryColor {
  Red = 'red',
  Green = 'green',
  Blue = 'blue',
}

export const ProjectSchema = z.object({
  id: ProjectIdSchema,
  subcategoryId: SubcategoryIdSchema,
  shortName: z.string(),
  shortNameDe: z.string().nullable(),
  name: z.string(),
  nameDe: z.string().nullable(),
  authors: z.array(z.string()),
  content: z.string(),
  contentDe: z.string().nullable(),
  shortContent: z.string(),
  shortContentDe: z.string().nullable(),
  contentFormat: z.nativeEnum(ContentFormat),
  doiLabel: z.string().nullable(),
  doiLink: z.string().nullable(),
  relations: z.record(ProjectIdSchema, z.number()),
  // todo fix datalayerstory and datalayerplain to one layer
  dataLayers: z
    .array(DataLayerDataSchema)
    .min(1)
    .or(z.tuple([DataLayerPlainSchema]))
    .or(z.tuple([DataLayerStorySchema])),
});
export type Project = z.infer<typeof ProjectSchema>;

// Schema for creating projects without requiring dataLayers
export const ProjectCreateSchema = ProjectSchema.omit({
  dataLayers: true,
  relations: true,
});
export type ProjectCreate = z.infer<typeof ProjectCreateSchema>;

export const SubcategorySchema = z.object({
  id: SubcategoryIdSchema,
  label: z.string(),
  labelDe: z.string().nullable(),
  description: z.string(),
  descriptionDe: z.string().nullable(),
  categoryId: z.string(),
});
export type Subcategory = z.infer<typeof SubcategorySchema>;

export const CategorySchema = z.object({
  id: CategoryIdSchema,
  label: z.string(),
  labelDe: z.string().nullable(),
  description: z.string(),
  descriptionDe: z.string().nullable(),
  color: z.nativeEnum(CategoryColor),
});
export type Category = z.infer<typeof CategorySchema>;

// Form schemas for validation
export const CategoryCreateSchema = CategorySchema;
export const CategoryUpdateSchema = CategorySchema.omit({ id: true });

export const SubcategoryCreateSchema = SubcategorySchema;
export const SubcategoryUpdateSchema = SubcategorySchema.omit({ id: true });

export const ProjectUpdateSchema = ProjectSchema.omit({ id: true });

export const GraphSchema = z.object({
  centralLabel: z.string(),
  categories: z.array(
    CategorySchema.extend({
      subcategories: z.array(SubcategorySchema),
    })
  ),
});
export type Graph = z.infer<typeof GraphSchema>;

export const GraphWithProjectsSchema = z.object({
  centralLabel: z.string(),
  categories: z.array(
    CategorySchema.extend({
      subcategories: z.array(
        SubcategorySchema.extend({ projects: z.array(ProjectSchema) })
      ),
    })
  ),
});
export type GraphWithProjects = z.infer<typeof GraphWithProjectsSchema>;

export const getGraph = async (signal: AbortSignal): Promise<Graph> => {
  // Fetch categories with subcategories from backend API
  const response = await fetch(`${BACKEND_BASE_URL}/api/hierarchy`, {
    signal,
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch categories: ${response.statusText}`);
  }

  const categories = await response.json();

  const graph = {
    centralLabel: 'Climate & Water\nUnder Change',
    categories: categories,
  };

  return GraphSchema.parse(graph);
};

export const getProjects = async (
  signal: AbortSignal
): Promise<{ projects: Project[] }> => {
  // Fetch projects from backend API
  const response = await fetch(`${BACKEND_BASE_URL}/api/projects`, {
    signal,
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch projects: ${response.statusText}`);
  }

  const projects = (await response.json()) as Omit<Project, 'relations'>[];

  const projectRelations: Record<ProjectId, Record<ProjectId, number>> = {
    cer: {
      'forest-model': 0,
      'planning-docs': 0,
      'lake-balance': 1,
      'flood-hazard': 0,
      'forest-health': 0,
      'resilience-assessment': 0,
      'forest-fires': 0,
      'legal-governance': 0,
      'legal-deterioration': 0,
      'legal-information': 0,
      'willingness-to-pay': 0,
      'soil-experiments': 0,
      'attitude-poll': 0,
      history: 0,
      'agr-impacts': 0,
      groundwater: 1,
      rainstorms: 1,
      anthropology: 0,
    },

    'forest-model': {
      cer: 0,
      'planning-docs': 0,
      'lake-balance': 0,
      'flood-hazard': 0,
      'forest-health': 1,
      'resilience-assessment': 0,
      'forest-fires': 1,
      'legal-governance': 0,
      'legal-deterioration': 0,
      'legal-information': 0,
      'willingness-to-pay': 0,
      'soil-experiments': 0,
      'attitude-poll': 0,
      history: 0,
      'agr-impacts': 0,
      groundwater: 0,
      rainstorms: 0,
      anthropology: 0,
    },

    'planning-docs': {
      'cer ': 0,
      'forest-model': 0,
      'lake-balance': 0,
      'flood-hazard': 0,
      'forest-health': 0,
      'resilience-assessment': 0,
      'forest-fires': 0,
      'legal-governance': 1,
      'legal-deterioration': 1,
      'legal-information': 0,
      'willingness-to-pay': 0,
      'soil-experiments': 0,
      'attitude-poll': 0,
      history: 0,
      'agr-impacts': 0,
      groundwater: 0,
      rainstorms: 0,
      anthropology: 0,
    },

    'lake-balance': {
      cer: 1,
      'forest-model': 0,
      'planning-docs': 0,
      'flood-hazard': 0,
      'forest-health': 0,
      'resilience-assessment': 1,
      'forest-fires': 0,
      'legal-governance': 0,
      'legal-deterioration': 1,
      'legal-information': 0,
      'willingness-to-pay': 1,
      'soil-experiments': 0,
      'attitude-poll': 0,
      history: 0,
      'agr-impacts': 0,
      groundwater: 0,
      rainstorms: 0,
      anthropology: 1,
    },

    'flood-hazard': {
      cer: 0,
      'forest-model': 0,
      'planning-docs': 0,
      'lake-balance': 0,
      'forest-health': 0,
      'resilience-assessment': 0,
      'forest-fires': 0,
      'legal-governance': 0,
      'legal-deterioration': 0,
      'legal-information': 1,
      'willingness-to-pay': 0,
      'soil-experiments': 0,
      'attitude-poll': 0,
      history: 1,
      'agr-impacts': 0,
      groundwater: 0,
      rainstorms: 1,
      anthropology: 0,
    },

    'forest-health': {
      cer: 0,
      'forest-model': 1,
      'planning-docs': 0,
      'lake-balance': 0,
      'flood-hazard': 0,
      'resilience-assessment': 0,
      'forest-fires': 1,
      'legal-governance': 0,
      'legal-deterioration': 0,
      'legal-information': 0,
      'willingness-to-pay': 0,
      'soil-experiments': 0,
      'attitude-poll': 0,
      history: 0,
      'agr-impacts': 1,
      groundwater: 0,
      rainstorms: 0,
      anthropology: 0,
    },

    'resilience-assessment': {
      cer: 0,
      'forest-model': 0,
      'planning-docs': 1,
      'lake-balance': 1,
      'flood-hazard': 1,
      'forest-health': 0,
      'forest-fires': 0,
      'legal-governance': 1,
      'legal-deterioration': 1,
      'legal-information': 1,
      'willingness-to-pay': 0,
      'soil-experiments': 0,
      'attitude-poll': 1,
      history: 0,
      'agr-impacts': 0,
      groundwater: 0,
      rainstorms: 0,
      anthropology: 0,
    },

    'forest-fires': {
      cer: 0,
      'forest-model': 1,
      'planning-docs': 0,
      'lake-balance': 0,
      'flood-hazard': 0,
      'forest-health': 1,
      'resilience-assessment': 0,
      'legal-governance': 0,
      'legal-deterioration': 0,
      'legal-information': 0,
      'willingness-to-pay': 0,
      'soil-experiments': 0,
      'attitude-poll': 0,
      history: 0,
      'agr-impacts': 0,
      groundwater: 0,
      rainstorms: 0,
      anthropology: 0,
    },

    'legal-governance': {
      cer: 0,
      'forest-model': 0,
      'planning-docs': 1,
      'lake-balance': 0,
      'flood-hazard': 0,
      'forest-health': 0,
      'resilience-assessment': 1,
      'forest-fires': 0,
      'legal-deterioration': 1,
      'legal-information': 1,
      'willingness-to-pay': 0,
      'soil-experiments': 0,
      'attitude-poll': 0,
      history: 0,
      'agr-impacts': 0,
      groundwater: 0,
      rainstorms: 0,
      anthropology: 0,
    },

    'legal-deterioration': {
      cer: 0,
      'forest-model': 0,
      'planning-docs': 0,
      'lake-balance': 1,
      'flood-hazard': 0,
      'forest-health': 0,
      'resilience-assessment': 1,
      'forest-fires': 0,
      'legal-governance': 1,
      'legal-information': 1,
      'willingness-to-pay': 0,
      'soil-experiments': 0,
      'attitude-poll': 0,
      history: 0,
      'agr-impacts': 0,
      groundwater: 0,
      rainstorms: 0,
      anthropology: 0,
    },
    'legal-information': {
      cer: 0,
      'forest-model': 0,
      'planning-docs': 0,
      'lake-balance': 0,
      'flood-hazard': 1,
      'forest-health': 0,
      'resilience-assessment': 1,
      'forest-fires': 0,
      'legal-governance': 1,
      'legal-deterioration': 1,
      'willingness-to-pay': 0,
      'soil-experiments': 0,
      'attitude-poll': 0,
      history: 1,
      'agr-impacts': 0,
      groundwater: 0,
      rainstorms: 1,
      anthropology: 0,
    },

    'willingness-to-pay': {
      cer: 0,
      'forest-model': 0,
      'planning-docs': 0,
      'lake-balance': 1,
      'flood-hazard': 0,
      'forest-health': 0,
      'resilience-assessment': 1,
      'forest-fires': 0,
      'legal-governance': 0,
      'legal-deterioration': 0,
      'legal-information': 0,
      'soil-experiments': 0,
      'attitude-poll': 1,
      history: 0,
      'agr-impacts': 0,
      groundwater: 0,
      rainstorms: 0,
      anthropology: 1,
    },

    'soil-experiments': {
      cer: 0,
      'forest-model': 0,
      'planning-docs': 0,
      'lake-balance': 0,
      'flood-hazard': 0,
      'forest-health': 0,
      'resilience-assessment': 0,
      'forest-fires': 0,
      'legal-governance': 0,
      'legal-deterioration': 0,
      'legal-information': 0,
      'willingness-to-pay': 0,
      'attitude-poll': 0,
      history: 0,
      'agr-impacts': 1,
      groundwater: 0,
      rainstorms: 0,
      anthropology: 0,
    },

    'attitude-poll': {
      cer: 0,
      'forest-model': 0,
      'planning-docs': 0,
      'lake-balance': 0,
      'flood-hazard': 0,
      'forest-health': 0,
      'resilience-assessment': 1,
      'forest-fires': 0,
      'legal-governance': 0,
      'legal-deterioration': 0,
      'legal-information': 0,
      'willingness-to-pay': 1,
      'soil-experiments': 0,
      history: 1,
      'agr-impacts': 0,
      groundwater: 0,
      rainstorms: 0,
      anthropology: 1,
    },

    history: {
      cer: 0,
      'forest-model': 0,
      'planning-docs': 0,
      'lake-balance': 0,
      'flood-hazard': 1,
      'forest-health': 0,
      'resilience-assessment': 0,
      'forest-fires': 0,
      'legal-governance': 0,
      'legal-deterioration': 0,
      'legal-information': 1,
      'willingness-to-pay': 0,
      'soil-experiments': 0,
      'attitude-poll': 1,
      'agr-impacts': 0,
      groundwater: 0,
      rainstorms: 1,
      anthropology: 1,
    },

    'agr-impacts': {
      cer: 0,
      'forest-model': 0,
      'planning-docs': 0,
      'lake-balance': 0,
      'flood-hazard': 0,
      'forest-health': 1,
      'resilience-assessment': 0,
      'forest-fires': 0,
      'legal-governance': 0,
      'legal-deterioration': 0,
      'legal-information': 0,
      'willingness-to-pay': 0,
      'soil-experiments': 1,
      'attitude-poll': 0,
      history: 0,
      groundwater: 0,
      rainstorms: 0,
      anthropology: 0,
    },

    groundwater: {
      cer: 1,
      'forest-model': 0,
      'planning-docs': 0,
      'lake-balance': 0,
      'flood-hazard': 0,
      'forest-health': 0,
      'resilience-assessment': 1,
      'forest-fires': 0,
      'legal-governance': 0,
      'legal-deterioration': 0,
      'legal-information': 0,
      'willingness-to-pay': 0,
      'soil-experiments': 0,
      'attitude-poll': 0,
      history: 0,
      'agr-impacts': 0,
      rainstorms: 0,
      anthropology: 0,
    },

    rainstorms: {
      cer: 1,
      'forest-model': 0,
      'planning-docs': 0,
      'lake-balance': 0,
      'flood-hazard': 1,
      'forest-health': 0,
      'resilience-assessment': 1,
      'forest-fires': 0,
      'legal-governance': 0,
      'legal-deterioration': 0,
      'legal-information': 1,
      'willingness-to-pay': 0,
      'soil-experiments': 0,
      'attitude-poll': 0,
      history: 1,
      'agr-impacts': 0,
      groundwater: 0,
      anthropology: 0,
    },

    anthropology: {
      cer: 0,
      'forest-model': 0,
      'planning-docs': 1,
      'lake-balance': 1,
      'flood-hazard': 0,
      'forest-health': 0,
      'resilience-assessment': 0,
      'forest-fires': 0,
      'legal-governance': 0,
      'legal-deterioration': 0,
      'legal-information': 0,
      'willingness-to-pay': 1,
      'soil-experiments': 0,
      'attitude-poll': 1,
      history: 0,
      'agr-impacts': 0,
      groundwater: 0,
      rainstorms: 0,
    },
  };

  ProjectSchema.extend({
    dataLayers: z
      .array(DataLayerDataSchema)
      .or(z.tuple([DataLayerPlainSchema]))
      .or(z.tuple([DataLayerStorySchema])),
  })
    .array()
    .parse(
      projects.map(
        (project, index: number) =>
          ({
            ...project,
            shortName: project.dataLayers.length
              ? project.shortName
              : `🚧 ${project.shortName}`,
            relations: projectRelations[project.id] || {},
          }) as Project
      )
    );

  return Promise.resolve({
    projects: projects.map(
      (project, index: number) =>
        ({
          ...project,
          shortName: project.dataLayers.length
            ? project.shortName
            : `🚧 ${project.shortName}`,
          relations: projectRelations[project.id] || {},
        }) as Project
    ),
  });
};

// Error handling helper
const handleApiError = async (response: Response, operation: string) => {
  if (!response.ok) {
    let errorMessage = `Failed to ${operation}`;
    try {
      const errorData = await response.json();
      if (errorData.error && errorData.message) {
        errorMessage = `${errorData.message} (${errorData.error})`;
      } else if (errorData.detail && errorData.detail.message) {
        errorMessage = `${errorData.detail.message} (${errorData.detail.error})`;
      } else {
        errorMessage = `${errorMessage}: ${response.status} ${response.statusText}`;
      }
    } catch {
      errorMessage = `${errorMessage}: ${response.status} ${response.statusText}`;
    }
    throw new Error(errorMessage);
  }
};

// CRUD operations for Categories
export const createCategory = async (
  category: Omit<Category, 'id'> & { id: string }
): Promise<Category> => {
  const response = await fetch(`${BACKEND_BASE_URL}/api/categories`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(category),
  });

  await handleApiError(response, 'create category');
  return response.json();
};

export const updateCategory = async (
  id: CategoryId,
  category: Omit<Category, 'id'>
): Promise<Category> => {
  const response = await fetch(`${BACKEND_BASE_URL}/api/categories/${id}`, {
    method: 'PUT',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ id, ...category }),
  });

  await handleApiError(response, 'update category');
  return response.json();
};

export const deleteCategory = async (id: CategoryId): Promise<void> => {
  const response = await fetch(`${BACKEND_BASE_URL}/api/categories/${id}`, {
    method: 'DELETE',
    credentials: 'include',
  });

  await handleApiError(response, 'delete category');
};

// CRUD operations for Subcategories
export const createSubcategory = async (
  subcategory: Omit<Subcategory, 'id'> & { id: string }
): Promise<Subcategory> => {
  const response = await fetch(`${BACKEND_BASE_URL}/api/subcategories`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(subcategory),
  });

  await handleApiError(response, 'create subcategory');
  return response.json();
};

export const updateSubcategory = async (
  id: SubcategoryId,
  subcategory: Omit<Subcategory, 'id'>
): Promise<Subcategory> => {
  const response = await fetch(`${BACKEND_BASE_URL}/api/subcategories/${id}`, {
    method: 'PUT',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ id, ...subcategory }),
  });

  await handleApiError(response, 'update subcategory');
  return response.json();
};

export const deleteSubcategory = async (id: SubcategoryId): Promise<void> => {
  const response = await fetch(`${BACKEND_BASE_URL}/api/subcategories/${id}`, {
    method: 'DELETE',
    credentials: 'include',
  });

  await handleApiError(response, 'delete subcategory');
};

// CRUD operations for Projects
export const createProject = async (
  project: ProjectCreate & { id: string }
): Promise<Project> => {
  const response = await fetch(`${BACKEND_BASE_URL}/api/projects`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(project),
  });

  await handleApiError(response, 'create project');
  return response.json();
};

export const updateProject = async (
  id: ProjectId,
  project: Omit<Project, 'id'>
): Promise<Project> => {
  const response = await fetch(`${BACKEND_BASE_URL}/api/projects/${id}`, {
    method: 'PUT',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ id, ...project }),
  });

  await handleApiError(response, 'update project');
  return response.json();
};

export const deleteProject = async (id: ProjectId): Promise<void> => {
  const response = await fetch(`${BACKEND_BASE_URL}/api/projects/${id}`, {
    method: 'DELETE',
    credentials: 'include',
  });

  await handleApiError(response, 'delete project');
};

// DataLayer CRUD operations through Project updates
export const updateDataLayer = async (
  projectId: ProjectId,
  dataLayerId: DataLayerId,
  updatedDataLayer: DataLayer | DataLayerPlain
): Promise<Project> => {
  // First get the current project
  const { projects } = await getProjects(new AbortController().signal);
  const project = projects.find((p) => p.id === projectId);

  if (!project) {
    throw new Error(`Project with id ${projectId} not found`);
  }

  // Update the specific dataLayer by id (not index)
  const updatedDataLayers = project.dataLayers.map((dataLayer) =>
    dataLayer.id === dataLayerId ? updatedDataLayer : dataLayer
  );

  // Create updated project and validate through schema
  const updatedProject = {
    ...project,
    dataLayers: updatedDataLayers,
  };

  // Validate the complete project through ProjectSchema
  const validatedProject = ProjectSchema.parse(updatedProject);

  // Update the project with validated data
  return updateProject(projectId, {
    content: validatedProject.content,
    contentDe: validatedProject.contentDe,
    shortName: validatedProject.shortName,
    shortNameDe: validatedProject.shortNameDe,
    subcategoryId: validatedProject.subcategoryId,
    name: validatedProject.name,
    nameDe: validatedProject.nameDe,
    authors: validatedProject.authors,
    shortContent: validatedProject.shortContent,
    shortContentDe: validatedProject.shortContentDe,
    contentFormat: validatedProject.contentFormat,
    doiLabel: validatedProject.doiLabel,
    doiLink: validatedProject.doiLink,
    relations: validatedProject.relations,
    dataLayers: validatedProject.dataLayers,
  });
};

export const createDataLayer = async (
  projectId: ProjectId,
  newDataLayer: DataLayer | DataLayerPlain
): Promise<Project> => {
  // First get the current project
  const { projects } = await getProjects(new AbortController().signal);
  const project = projects.find((p) => p.id === projectId);

  if (!project) {
    throw new Error(`Project with id ${projectId} not found`);
  }

  // Add the new dataLayer to the existing array
  const updatedDataLayers = [...project.dataLayers, newDataLayer];

  // Create updated project and validate through schema
  const updatedProject = {
    ...project,
    dataLayers: updatedDataLayers,
  };

  // Validate the complete project through ProjectSchema
  const validatedProject = ProjectSchema.parse(updatedProject);

  // Update the project with validated data
  return updateProject(projectId, {
    content: validatedProject.content,
    contentDe: validatedProject.contentDe,
    shortName: validatedProject.shortName,
    shortNameDe: validatedProject.shortNameDe,
    subcategoryId: validatedProject.subcategoryId,
    name: validatedProject.name,
    nameDe: validatedProject.nameDe,
    authors: validatedProject.authors,
    shortContent: validatedProject.shortContent,
    shortContentDe: validatedProject.shortContentDe,
    contentFormat: validatedProject.contentFormat,
    doiLabel: validatedProject.doiLabel,
    doiLink: validatedProject.doiLink,
    relations: validatedProject.relations,
    dataLayers: validatedProject.dataLayers,
  });
};

export const deleteDataLayer = async (
  projectId: ProjectId,
  dataLayerId: DataLayerId
): Promise<Project> => {
  // First get the current project
  const { projects } = await getProjects(new AbortController().signal);
  const project = projects.find((p) => p.id === projectId);

  if (!project) {
    throw new Error(`Project with id ${projectId} not found`);
  }

  // Remove the specific dataLayer by id (not index)
  const updatedDataLayers = project.dataLayers.filter(
    (dataLayer) => dataLayer.id !== dataLayerId
  );

  // Create updated project and validate through schema
  const updatedProject = {
    ...project,
    dataLayers: updatedDataLayers,
  };

  // Validate the complete project through ProjectSchema
  const validatedProject = ProjectSchema.parse(updatedProject);

  // Update the project with validated data
  return updateProject(projectId, {
    content: validatedProject.content,
    contentDe: validatedProject.contentDe,
    shortName: validatedProject.shortName,
    shortNameDe: validatedProject.shortNameDe,
    subcategoryId: validatedProject.subcategoryId,
    name: validatedProject.name,
    nameDe: validatedProject.nameDe,
    authors: validatedProject.authors,
    shortContent: validatedProject.shortContent,
    shortContentDe: validatedProject.shortContentDe,
    contentFormat: validatedProject.contentFormat,
    doiLabel: validatedProject.doiLabel,
    doiLink: validatedProject.doiLink,
    relations: validatedProject.relations,
    dataLayers: validatedProject.dataLayers,
  });
};
